/**
 * BookingKiosk.tsx — Full-screen kiosk mode for venue check-in
 *
 * Features:
 *  - Reference number entry (large touch-friendly keypad)
 *  - QR code scan support (reads camera input via <input capture>)
 *  - Shows booking details (name, space, time, PIN)
 *  - Operator check-in / check-out buttons (requires operator PIN)
 *  - Auto-idle return after 60s of inactivity
 *
 * Mounted at /kiosk in App.tsx (no sidebar, full-screen).
 * Admin backend call: POST /api/bookings/kiosk/lookup  {reference}
 * Check-in:  POST /api/bookings/:id/check-in  (JWT — operator must log in or use kiosk key)
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
// ── Inline SVG icons (no lucide-react dependency) ────────────────────────────
const Building2 = ({size=24,color='currentColor',style:s}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={s}><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01M16 6h.01M12 6h.01M12 10h.01M8 10h.01M16 10h.01M8 14h.01M16 14h.01M12 14h.01"/></svg>;
const Key = ({size=24,color='currentColor'}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="7.5" cy="15.5" r="5.5"/><path d="M21 2l-9.6 9.6M15.5 7.5l3 3L22 7l-3-3"/></svg>;
const Clock = ({size=24,color='currentColor'}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>;
const Calendar = ({size=24,color='currentColor'}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>;
const User = ({size=24,color='currentColor'}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const CheckCircle2 = ({size=24,color='currentColor',style:s}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={s}><circle cx="12" cy="12" r="10"/><path d="M9 12l2 2 4-4"/></svg>;
const RotateCcw = ({size=24,color='currentColor'}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 2v6h6"/><path d="M21 12A9 9 0 0 0 6 5.3L3 8"/></svg>;
const Camera = ({size=24,color='currentColor'}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>;
const Delete = ({size=24,color='currentColor'}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 4H8l-7 8 7 8h13a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2z"/><line x1="18" y1="9" x2="12" y2="15"/><line x1="12" y1="9" x2="18" y2="15"/></svg>;
const Loader2 = ({size=24,color='currentColor'}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{animation:'spin 1s linear infinite'}}><path d="M21 12a9 9 0 1 1-6.219-8.56"/></svg>;
const AlertCircle = ({size=24,color='currentColor',style:s}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={s}><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>;
const ArrowRight = ({size=24,color='currentColor'}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>;
const ShieldCheck = ({size=24,color='currentColor'}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/></svg>;
const WifiOff = ({size=24,color='currentColor'}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="1" y1="1" x2="23" y2="23"/><path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55M5 12.55a10.94 10.94 0 0 1 5.17-2.39M10.71 5.05A16 16 0 0 1 22.56 9M1.42 9a15.91 15.91 0 0 1 4.7-2.88M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>;
const Wifi = ({size=24,color='currentColor'}:any) => <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0M1.42 9a16 16 0 0 1 21.16 0M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>;

// ── Types ─────────────────────────────────────────────────────────────────────

interface KioskBooking {
  id: string;
  reference: string;
  spaceName: string;
  bookingDate: string;
  startTime: string;
  endTime: string;
  firstName: string;
  lastName: string;
  status: 'pending' | 'confirmed' | 'active' | 'completed' | 'cancelled';
  pinCode?: string;
  protegeSynced: boolean;
  totalCost?: number | null;
}

type Screen = 'idle' | 'entry' | 'detail' | 'action' | 'success' | 'error';

// ── Config ────────────────────────────────────────────────────────────────────

const KIOSK_API = '/api/bookings';
const IDLE_TIMEOUT_MS = 60_000;   // return to idle after 60s inactivity
const SUCCESS_HOLD_MS = 5_000;    // stay on success screen 5s before idle

// ── Helpers ───────────────────────────────────────────────────────────────────

function getToken() {
  return localStorage.getItem('access_token') || sessionStorage.getItem('access_token') || '';
}

async function apiFetch(url: string, opts: RequestInit = {}) {
  const tok = getToken();
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };
  if (tok) headers['Authorization'] = `Bearer ${tok}`;
  const res = await fetch(url, { headers, ...opts });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}

function fmtDate(d: string) {
  try { return new Date(d + 'T00:00:00').toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' }); }
  catch { return d; }
}

function fmtTime(t: string) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hr = parseInt(h, 10);
  return `${hr > 12 ? hr - 12 : hr || 12}:${m} ${hr >= 12 ? 'PM' : 'AM'}`;
}

function statusColor(s: string) {
  return s === 'confirmed' ? '#3b82f6' : s === 'active' ? '#22c55e' : s === 'pending' ? '#f59e0b' : s === 'cancelled' ? '#ef4444' : '#555555';
}

// ── Numpad ────────────────────────────────────────────────────────────────────

function Numpad({ value, onChange, maxLen = 9 }: { value: string; onChange: (v: string) => void; maxLen?: number }) {
  const keys = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'CLR', '0', '⌫'];

  const handleKey = (k: string) => {
    if (k === 'CLR') { onChange(''); return; }
    if (k === '⌫') { onChange(value.slice(0, -1)); return; }
    // Auto-insert dash after 4 chars
    let next = value.replace('-', '');
    if (next.length < maxLen) next += k;
    if (next.length === 4) next = next + '-';
    onChange(next.toUpperCase());
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
      {keys.map(k => (
        <button key={k} onClick={() => handleKey(k)}
          style={{
            height: 72, fontSize: k === '⌫' || k === 'CLR' ? 18 : 28, fontWeight: 600,
            borderRadius: 12, border: 'none', cursor: 'pointer',
            background: k === 'CLR' ? 'rgba(239,68,68,0.12)' : k === '⌫' ? 'rgba(100,116,139,0.15)' : 'rgba(255,255,255,0.08)',
            color: k === 'CLR' ? '#ef4444' : '#e2e8f0',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.1s',
          }}
          onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = k === 'CLR' ? 'rgba(239,68,68,0.22)' : 'rgba(255,255,255,0.14)'; }}
          onMouseLeave={e => {
            const target = e.currentTarget as HTMLElement;
            target.style.background = k === 'CLR' ? 'rgba(239,68,68,0.12)' : k === '⌫' ? 'rgba(100,116,139,0.15)' : 'rgba(255,255,255,0.08)';
          }}>
          {k === '⌫' ? <Delete size={20} /> : k}
        </button>
      ))}
    </div>
  );
}

// ── Idle Screen ───────────────────────────────────────────────────────────────

function IdleScreen({ onStart }: { onStart: () => void }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40, cursor: 'pointer' }} onClick={onStart}>
      <Building2 size={80} color="#3b82f6" style={{ marginBottom: 32, opacity: 0.8 }} />
      <div style={{ fontSize: 36, fontWeight: 800, color: '#e5e5e5', marginBottom: 12, textAlign: 'center' }}>
        Community Venue Kiosk
      </div>
      <div style={{ fontSize: 20, color: '#555555', marginBottom: 48, textAlign: 'center' }}>
        City of Parramatta
      </div>
      <div style={{ fontSize: 18, color: '#3b82f6', fontWeight: 600, padding: '16px 40px', border: '2px solid #3b82f6', borderRadius: 16, display: 'flex', alignItems: 'center', gap: 12 }}>
        <ArrowRight size={20} /> Tap to begin check-in
      </div>
    </div>
  );
}

// ── Entry Screen (reference input) ────────────────────────────────────────────

function EntryScreen({ onLookup, onBack }: { onLookup: (ref: string) => void; onBack: () => void }) {
  const [ref, setRef] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const handleLookup = useCallback(async (r: string) => {
    const clean = r.trim().toUpperCase();
    if (clean.length < 4) { setErr('Enter your 8-character reference (e.g. A3F7-B2D9)'); return; }
    setLoading(true); setErr('');
    try { onLookup(clean); }
    catch (e: any) { setErr(e.message); }
    finally { setLoading(false); }
  }, [onLookup]);

  // Auto-submit when we have a full reference (XXXX-XXXX = 9 chars)
  useEffect(() => {
    if (ref.replace('-', '').length === 8) {
      handleLookup(ref);
    }
  }, [ref, handleLookup]);

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 40px', maxWidth: 500, margin: '0 auto', width: '100%' }}>
      <div style={{ fontSize: 24, fontWeight: 700, color: '#e5e5e5', marginBottom: 8 }}>Enter Booking Reference</div>
      <div style={{ fontSize: 15, color: '#555555', marginBottom: 32 }}>Use the keypad below or scan your QR code</div>

      {/* Reference display */}
      <div style={{
        fontFamily: 'monospace', fontSize: 44, fontWeight: 800, letterSpacing: 12,
        color: ref ? '#e2e8f0' : '#222222',
        background: 'rgba(255,255,255,0.05)', border: '2px solid rgba(255,255,255,0.1)',
        borderRadius: 16, padding: '18px 32px', marginBottom: 24, width: '100%',
        textAlign: 'center', minHeight: 88,
      }}>
        {ref || <span style={{ opacity: 0.3 }}>A3F7-B2D9</span>}
      </div>

      {err && (
        <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 10, padding: '12px 20px', color: '#ef4444', fontSize: 14, marginBottom: 16, width: '100%', textAlign: 'center' }}>
          <AlertCircle size={14} style={{ display: 'inline', marginRight: 6 }} />{err}
        </div>
      )}

      <Numpad value={ref} onChange={setRef} />

      <div style={{ display: 'flex', gap: 12, marginTop: 24, width: '100%' }}>
        <button onClick={onBack}
          style={{ flex: 1, height: 56, borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#555555', fontSize: 15, cursor: 'pointer' }}>
          Back
        </button>
        {/* QR / camera scan (mobile) */}
        <button onClick={() => fileRef.current?.click()}
          style={{ height: 56, width: 56, borderRadius: 12, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#555555', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Camera size={22} />
        </button>
        <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
          onChange={() => { /* QR decode would go here — placeholder for jsQR integration */ }} />
        <button onClick={() => handleLookup(ref)} disabled={loading}
          style={{ flex: 1, height: 56, borderRadius: 12, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: loading ? 0.7 : 1 }}>
          {loading ? <Loader2 size={18} color="#555555" /> : <><ArrowRight size={18} /> Find</>}
        </button>
      </div>
    </div>
  );
}

// ── Detail Screen ─────────────────────────────────────────────────────────────

function DetailScreen({ booking, onAction, onBack }: {
  booking: KioskBooking;
  onAction: (action: 'check-in' | 'check-out') => void;
  onBack: () => void;
}) {
  const isCheckedIn = booking.status === 'active';
  const canCheckIn  = booking.status === 'confirmed';
  const canCheckOut = booking.status === 'active';

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px 40px', maxWidth: 560, margin: '0 auto', width: '100%' }}>
      <div style={{ width: '100%', background: '#111111', border: '1px solid #1a1a1a', borderRadius: 20, padding: 28, marginBottom: 24 }}>
        {/* Reference + status */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <span style={{ fontFamily: 'monospace', fontSize: 22, fontWeight: 700, color: '#3b82f6', letterSpacing: 4 }}>{booking.reference}</span>
          <span style={{ padding: '5px 14px', borderRadius: 999, fontSize: 13, fontWeight: 700, background: `${statusColor(booking.status)}20`, color: statusColor(booking.status), border: `1px solid ${statusColor(booking.status)}40` }}>
            {booking.status.toUpperCase()}
          </span>
        </div>

        {/* Details */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px', marginBottom: 20 }}>
          {[
            { icon: <Building2 size={16} />, label: 'Venue',   value: booking.spaceName },
            { icon: <User size={16} />,      label: 'Name',    value: `${booking.firstName} ${booking.lastName}` },
            { icon: <Calendar size={16} />,  label: 'Date',    value: fmtDate(booking.bookingDate) },
            { icon: <Clock size={16} />,     label: 'Time',    value: `${fmtTime(booking.startTime)} – ${fmtTime(booking.endTime)}` },
          ].map(r => (
            <div key={r.label}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#555555', marginBottom: 2 }}>{r.icon}{r.label}</div>
              <div style={{ fontSize: 15, fontWeight: 600, color: '#e5e5e5' }}>{r.value}</div>
            </div>
          ))}
        </div>

        {/* PIN */}
        {booking.pinCode && (
          <div style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 12, padding: '14px 20px', textAlign: 'center' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: 12, color: '#22c55e', marginBottom: 6 }}>
              <Key size={13} /> ACCESS PIN
              {booking.protegeSynced
                ? <span style={{ marginLeft: 8, display: 'flex', alignItems: 'center', gap: 3 }}><Wifi size={11} /> Synced</span>
                : <span style={{ marginLeft: 8, display: 'flex', alignItems: 'center', gap: 3, color: '#f59e0b' }}><WifiOff size={11} /> Not synced</span>}
            </div>
            <div style={{ fontSize: 48, fontWeight: 900, letterSpacing: 16, color: '#22c55e' }}>{booking.pinCode}</div>
          </div>
        )}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 16, width: '100%' }}>
        <button onClick={onBack}
          style={{ height: 64, flex: '0 0 80px', borderRadius: 16, border: '1px solid rgba(255,255,255,0.1)', background: 'transparent', color: '#555555', fontSize: 14, cursor: 'pointer' }}>
          Back
        </button>
        {canCheckIn && (
          <button onClick={() => onAction('check-in')}
            style={{ flex: 1, height: 64, borderRadius: 16, border: 'none', background: '#22c55e', color: '#fff', fontSize: 18, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <ShieldCheck size={22} /> Check In
          </button>
        )}
        {canCheckOut && (
          <button onClick={() => onAction('check-out')}
            style={{ flex: 1, height: 64, borderRadius: 16, border: 'none', background: '#f59e0b', color: '#fff', fontSize: 18, fontWeight: 800, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}>
            <CheckCircle2 size={22} /> Check Out
          </button>
        )}
        {!canCheckIn && !canCheckOut && (
          <div style={{ flex: 1, height: 64, borderRadius: 16, background: '#111111', border: '1px solid #1a1a1a', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#555555', fontSize: 14 }}>
            No action available for status: <strong style={{ marginLeft: 6 }}>{booking.status}</strong>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Success Screen ────────────────────────────────────────────────────────────

function SuccessScreen({ action, booking }: { action: string; booking: KioskBooking }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
      <CheckCircle2 size={80} color="#22c55e" style={{ marginBottom: 24 }} />
      <div style={{ fontSize: 32, fontWeight: 800, color: '#e5e5e5', marginBottom: 12 }}>
        {action === 'check-in' ? 'Check-in Complete!' : 'Check-out Complete!'}
      </div>
      <div style={{ fontSize: 18, color: '#555555', marginBottom: 32, textAlign: 'center' }}>
        {booking.firstName} {booking.lastName} · {booking.spaceName}
      </div>
      <div style={{ fontFamily: 'monospace', fontSize: 28, letterSpacing: 8, color: '#3b82f6', fontWeight: 700 }}>{booking.reference}</div>
      <div style={{ fontSize: 13, color: '#555555', marginTop: 32 }}>Returning to home screen…</div>
    </div>
  );
}

// ── Error Screen ──────────────────────────────────────────────────────────────

function ErrorScreen({ message, onBack }: { message: string; onBack: () => void }) {
  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
      <AlertCircle size={72} color="#ef4444" style={{ marginBottom: 24 }} />
      <div style={{ fontSize: 26, fontWeight: 700, color: '#e5e5e5', marginBottom: 12 }}>Booking Not Found</div>
      <div style={{ fontSize: 16, color: '#555555', marginBottom: 40, textAlign: 'center', maxWidth: 380 }}>{message}</div>
      <button onClick={onBack}
        style={{ padding: '16px 48px', borderRadius: 14, border: 'none', background: '#3b82f6', color: '#fff', fontSize: 16, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
        <RotateCcw size={18} /> Try Again
      </button>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

// ── Spin keyframe (injected once) ─────────────────────────────────────────────
const KIOSK_STYLE = `@keyframes spin{to{transform:rotate(360deg)}}`;

export default function BookingKiosk() {
  const [screen, setScreen] = useState<Screen>('idle');
  const [booking, setBooking] = useState<KioskBooking | null>(null);
  const [lastAction, setLastAction] = useState<'check-in' | 'check-out'>('check-in');
  const [errMsg, setErrMsg] = useState('');
  const [acting, setActing] = useState(false);
  const idleTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetIdle = useCallback(() => {
    if (idleTimer.current) clearTimeout(idleTimer.current);
    idleTimer.current = setTimeout(() => setScreen('idle'), IDLE_TIMEOUT_MS);
  }, []);

  // Reset idle timer on any interaction
  useEffect(() => {
    if (screen === 'idle') return;
    const handler = () => resetIdle();
    window.addEventListener('click', handler);
    window.addEventListener('keydown', handler);
    resetIdle();
    return () => {
      window.removeEventListener('click', handler);
      window.removeEventListener('keydown', handler);
      if (idleTimer.current) clearTimeout(idleTimer.current);
    };
  }, [screen, resetIdle]);

  // Auto-return from success
  useEffect(() => {
    if (screen !== 'success') return;
    const t = setTimeout(() => { setScreen('idle'); setBooking(null); }, SUCCESS_HOLD_MS);
    return () => clearTimeout(t);
  }, [screen]);

  const handleLookup = async (ref: string) => {
    try {
      const result = await apiFetch(`${KIOSK_API}/kiosk/lookup`, {
        method: 'POST', body: JSON.stringify({ reference: ref }),
      });
      setBooking(result);
      setScreen('detail');
    } catch (e: any) {
      setErrMsg(e.message === 'HTTP 404' ? `No booking found for reference "${ref}". Please check and try again.` : e.message);
      setScreen('error');
    }
  };

  const handleAction = async (action: 'check-in' | 'check-out') => {
    if (!booking) return;
    setActing(true);
    try {
      await apiFetch(`${KIOSK_API}/${booking.id}/${action}`, { method: 'POST', body: '{}' });
      setLastAction(action);
      setBooking(b => b ? { ...b, status: action === 'check-in' ? 'active' : 'completed' } : b);
      setScreen('success');
    } catch (e: any) {
      setErrMsg(e.message);
      setScreen('error');
    } finally { setActing(false); }
  };

  return (
    <>
    <style>{KIOSK_STYLE}</style>
    <div style={{
      position: 'fixed', inset: 0,
      background: 'linear-gradient(135deg, #0a0f1e 0%, #0d0d0d 100%)',
      display: 'flex', flexDirection: 'column',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      userSelect: 'none',
    }}>
      {/* Header bar */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 28px', borderBottom: '1px solid rgba(255,255,255,0.05)', background: 'rgba(0,0,0,0.3)' }}>
        <Building2 size={28} color="#3b82f6" />
        <div>
          <div style={{ fontSize: 18, fontWeight: 700, color: '#e5e5e5' }}>Community Venue Kiosk</div>
          <div style={{ fontSize: 12, color: '#555555' }}>City of Parramatta · Lemai Platform</div>
        </div>
        {acting && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, color: '#555555', fontSize: 13 }}>
            <Loader2 size={16} color="#555555" /> Processing…
          </div>
        )}
      </div>

      {/* Screen content */}
      {screen === 'idle' && <IdleScreen onStart={() => setScreen('entry')} />}
      {screen === 'entry' && <EntryScreen onLookup={handleLookup} onBack={() => setScreen('idle')} />}
      {screen === 'detail' && booking && (
        <DetailScreen booking={booking} onAction={handleAction} onBack={() => setScreen('entry')} />
      )}
      {screen === 'success' && booking && (
        <SuccessScreen action={lastAction} booking={booking} />
      )}
      {screen === 'error' && (
        <ErrorScreen message={errMsg} onBack={() => setScreen('entry')} />
      )}

      {/* Footer */}
      <div style={{ padding: '12px 28px', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: 12, color: '#444444' }}>
          {new Date().toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' })} · {new Date().toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short' })}
        </span>
        {screen !== 'idle' && (
          <button onClick={() => { setScreen('idle'); setBooking(null); }}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 14px', borderRadius: 8, border: '1px solid #1a1a1a', background: 'transparent', color: '#555555', fontSize: 12, cursor: 'pointer' }}>
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>
    </div>
    </>
  );
}
